import { createContext, useContext, useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { APPEARANCE_VIEW_VERSION, CARD_CSS, type WordingKey } from '@tikkas/shared';
import { logoFor, readView, say, STARTING, themeCss, viewForNow, type View } from './appearance.js';
import { apiFetch } from './api.js';
import { apiUrl } from './platform.js';

/*
 * The admin's appearance settings in the running app (docs/26 §1). The last answer of GET /api/appearance is kept on
 * the phone and drawn before the first paint (main.tsx), so the app opens at once, offline too, in the look it had;
 * then the server is asked again quietly (at start, on coming back to the app, every minute while open and at
 * nextChangeAt, when a special starts or ends) and a new look is applied in place. With nothing kept yet, the
 * starting look from @tikkas/shared.
 */

// The key carries the shape's version: a copy kept by an older app build is left alone, never read as the new shape.
const KEY = `tikkas-look-v${APPEARANCE_VIEW_VERSION}`;
const EVERY_MS = 60_000;

interface Kept { etag: string | null; view: View; everyday: View['theme'] | null }

function readKept(): Kept | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const k = JSON.parse(raw) as Partial<Kept>;
    return { etag: typeof k.etag === 'string' ? k.etag : null, view: readView(k.view), everyday: k.everyday ? readView({ theme: k.everyday }).theme : null };
  } catch {
    return null; // unreadable: the starting look, and a fresh answer replaces it
  }
}
function keep(k: Kept): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(k));
  } catch {
    /* private mode: drawn now, just not kept */
  }
}

/** The look to draw at start: the kept one (a special that ended while offline gives way to the everyday theme), else the starting one. */
export function keptLook(now = new Date()): View {
  const k = readKept();
  return k ? viewForNow(k.view, k.everyday, now) : STARTING;
}

/** The theme as one style element (empty for today's colours); the browser's bar colour follows the page. */
export function applyLook(v: View): void {
  // The Tikkas card's layers and animations (shared with the admin's preview), once.
  if (!document.getElementById('tikkas-card-css')) {
    const cardCss = document.createElement('style');
    cardCss.id = 'tikkas-card-css';
    cardCss.textContent = CARD_CSS;
    document.head.appendChild(cardCss);
  }
  let el = document.getElementById('tikkas-theme');
  if (!el) {
    el = document.createElement('style');
    el.id = 'tikkas-theme';
    document.head.appendChild(el);
  }
  const css = themeCss(v);
  if (el.textContent !== css) el.textContent = css;
  // The look's CSS hooks (styles.css): the tab bar's shape, the material, the background and motion, set before the first paint like the colours.
  const h = document.documentElement;
  h.setAttribute('data-tabbar', v.theme.tabBar);
  h.setAttribute('data-material', v.theme.material);
  h.setAttribute('data-bg', v.theme.background);
  h.setAttribute('data-motion', v.theme.motion ? 'on' : 'off');
  syncThemeColour();
}

/** The theme-colour meta tag (the browser bar, the status bar on Android) takes the page background. */
export function syncThemeColour(): void {
  const meta = document.querySelector('meta[name="theme-color"]');
  const bg = getComputedStyle(document.documentElement).getPropertyValue('--bg').trim();
  if (meta && bg) meta.setAttribute('content', bg);
}

interface Look {
  v: View;
  /** The wording in force for a key (docs/26 §6). */
  t: (k: WordingKey) => string;
}
const LookContext = createContext<Look>({ v: STARTING, t: (k) => say(STARTING, k) });

export function LookProvider({ children }: { children: ReactNode }) {
  const [v, setV] = useState<View>(keptLook);
  const drawn = useRef(JSON.stringify(v));

  useEffect(() => {
    let last = 0;
    let busy = false;
    let at: number | null = null;
    const show = (next: View) => {
      const json = JSON.stringify(next);
      if (json === drawn.current) return;
      drawn.current = json;
      applyLook(next);
      setV(next);
    };
    // A special starts or ends at nextChangeAt: ask again just after, so it switches on time.
    const plan = (next: string | null) => {
      if (at != null) clearTimeout(at);
      at = null;
      const wait = next ? Date.parse(next) - Date.now() + 1500 : NaN;
      if (wait > 0 && wait < EVERY_MS * 60 * 24) at = window.setTimeout(() => load(true), wait);
    };
    const load = async (force = false) => {
      if (busy || document.visibilityState !== 'visible' || (!force && Date.now() - last < EVERY_MS - 1000)) return;
      busy = true;
      last = Date.now();
      try {
        const kept = readKept();
        // The ETag says whether anything changed since the copy kept here; 304 means keep it.
        const res = await apiFetch('/api/appearance', { headers: kept?.etag ? { 'if-none-match': kept.etag } : {} });
        if (res.status === 304 && kept) {
          plan(kept.view.nextChangeAt);
          return show(viewForNow(kept.view, kept.everyday, new Date()));
        }
        if (!res.ok) return; // not deployed yet, or a hiccup: the look on screen stays
        const view = readView(await res.json());
        keep({ etag: res.headers.get('etag'), view, everyday: view.theme.id === 'everyday' ? view.theme : kept?.everyday ?? null });
        plan(view.nextChangeAt);
        show(view);
      } catch {
        /* offline: the look on screen stays */
      } finally {
        busy = false;
      }
    };
    void load(true);
    const tick = window.setInterval(() => void load(), EVERY_MS);
    const onShow = () => void load();
    document.addEventListener('visibilitychange', onShow);
    return () => {
      clearInterval(tick);
      if (at != null) clearTimeout(at);
      document.removeEventListener('visibilitychange', onShow);
    };
  }, []);

  return <LookContext.Provider value={{ v, t: (k) => say(v, k) }}>{children}</LookContext.Provider>;
}

export const useLook = (): Look => useContext(LookContext);

// The glow's drift pauses while the tab is hidden (nothing animates for nobody).
if (typeof document !== 'undefined') document.addEventListener('visibilitychange', () => document.documentElement.toggleAttribute('data-hidden', document.visibilityState !== 'visible'));

/** The Tikkas wordmark, or the theme's logo (from our API, by its address in the app); the built-in one stands in if the theme's can't load. */
export function Logo({ className, style, ...rest }: { className?: string; style?: CSSProperties; 'data-intro-target'?: string }) {
  const { v } = useLook();
  const custom = logoFor(v);
  const [failed, setFailed] = useState<string | null>(null);
  const standIn = !custom || failed === custom;
  return <img {...rest} className={className} style={style} src={standIn ? '/logo.webp' : apiUrl(custom)} alt="Tikkas" data-std={custom && standIn ? '' : undefined} onError={() => custom && setFailed(custom)} />;
}

/** The logo image's address for a mask (the card draws the logo in its design's colour): the theme's upload, or the built-in wordmark. */
export function logoUrlFor(v: View): string {
  const custom = logoFor(v);
  return custom ? apiUrl(custom) : '/logo.webp';
}
